import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import {
  type ContentLanguage,
  addContentLanguageAction,
  assert,
  removeLanguageAction,
  updateContentLanguageAction,
} from "@skilitics/nautilus-core-designer-data";

import { useContentLanguagesForContentApi } from "../backend/index.ts";
import { useAvailableLanguages } from "./useAvailableLanguages.ts";

export const useAddContentLanguage = () => {
  const { add } = useContentLanguagesForContentApi();
  const languages = useAvailableLanguages();
  const dispatch = useDispatch();
  const [pending, setPending] = useState(false);

  const addLanguage = useCallback(
    async (contentLang: Omit<ContentLanguage, "language">) => {
      const language = languages.find((l) => l.code === contentLang.code);
      assert(language, `Language with code ${contentLang.code} not found in available languages.`);
      setPending(true);
      try {
        const result = await add(contentLang);
        dispatch(addContentLanguageAction(result));
      } finally {
        setPending(false);
      }
    },
    [add, dispatch, languages],
  );

  return useMemo(() => ({ addLanguage, isAdding: pending }), [addLanguage, pending]);
};

// Removal is a node designs action: it strips the locale out of the node configs, so it goes through the
// revision queue like any other design edit rather than an API call of its own. The server deletes the
// content language row as part of applying that revision.
export const useRemoveContentLanguage = () => {
  const dispatch = useDispatch();

  const removeLanguage = useCallback(
    (code: string) => {
      dispatch(removeLanguageAction({ lang: code }));
    },
    [dispatch],
  );

  return useMemo(() => ({ removeLanguage }), [removeLanguage]);
};

export const useUpdateContentLanguage = () => {
  const { update } = useContentLanguagesForContentApi();
  const [pending, setPending] = useState(false);
  const dispatch = useDispatch();

  const updateLanguage = useCallback(
    async (payload: Parameters<typeof update>[0]) => {
      setPending(true);
      try {
        await update(payload);
        dispatch(updateContentLanguageAction(payload));
      } finally {
        setPending(false);
      }
    },
    [dispatch, update],
  );

  return useMemo(() => ({ updateLanguage, isUpdating: pending }), [updateLanguage, pending]);
};
